import { z } from "zod";
import { UIElementType } from "../../../AppConstants";
import { t } from "i18next";

const getBranchCostConfigSchema = (branchData) => {
    return z.object({
        Branch: z
            .object({
                id: z.number(),
                label: z.string(),
            })
            .nullable()
            .refine((value) => value !== null && value?.id !== null, {
                message: "Branch is required",
            })
            .describe(
                JSON.stringify({
                    elementType: UIElementType.DROPDOWN,
                    inputLabel: t("createBranchCostConfig.branch"),
                    isMultiSelect: false,
                    isRequired: true,
                    options: branchData
                })
            ),
        MinuteCostDay: z.union([z.literal(0), z.number().positive()])
            .nullable()
            .refine(value => value === null || value >= 0, {
                message: "If entered, the number must be positive or zero",
            }).describe(
                JSON.stringify({
                    inputType: "number",
                    elementType: UIElementType.TEXTFIELD,
                    inputLabel: t("createBranchCostConfig.minuteCostDay"),
                    isRequired: true,
                })
            ),
        MinuteCostNight: z.union([z.literal(0), z.number().positive()])
            .nullable()
            .refine(value => value === null || value >= 0, {
                message: "If entered, the number must be positive or zero",
            }).describe(
                JSON.stringify({
                    inputType: "number",
                    elementType: UIElementType.TEXTFIELD,
                    inputLabel: t("createBranchCostConfig.minuteCostNight"),
                    isRequired: true,
                })
            ),
        MinuteCostHoliday: z.union([z.literal(0), z.number().positive()])
            .nullable()
            .refine(value => value === null || value >= 0, {
                message: "If entered, the number must be positive or zero",
            }).describe(
                JSON.stringify({
                    inputType: "number",
                    elementType: UIElementType.TEXTFIELD,
                    inputLabel: t("createBranchCostConfig.minuteCostHoliday"),
                    isRequired: true,
                })
            ),
        MinuteCostHolidayNight: z.union([z.literal(0), z.number().positive()])
            .nullable()
            .refine(value => value === null || value >= 0, {
                message: "If entered, the number must be positive or zero",
            }).describe(
                JSON.stringify({
                    inputType: "number",
                    elementType: UIElementType.TEXTFIELD,
                    inputLabel: t("createBranchCostConfig.minuteCostHolidayNight"),
                    isRequired: true,
                })
            ),
        VisitCost: z.union([z.literal(0), z.number().positive()])
            .nullable()
            .refine(value => value === null || value >= 0, {
                message: "If entered, the number must be positive or zero",
            }).describe(
                JSON.stringify({
                    inputType: "number",
                    elementType: UIElementType.TEXTFIELD,
                    inputLabel: t("createBranchCostConfig.visitCost"),
                    isRequired: true,
                })
            ),
        VistCostNight: z.union([z.literal(0), z.number().positive()])
            .nullable()
            .refine(value => value === null || value >= 0, {
                message: "If entered, the number must be positive or zero",
            }).describe(
                JSON.stringify({
                    inputType: "number",
                    elementType: UIElementType.TEXTFIELD,
                    inputLabel: t("createBranchCostConfig.visitCostNight"),
                    isRequired: true,
                })
            ),
        VisitCostHoliday: z.union([z.literal(0), z.number().positive()])
            .nullable()
            .refine(value => value === null || value >= 0, {
                message: "If entered, the number must be positive or zero",
            }).describe(
                JSON.stringify({
                    inputType: "number",
                    elementType: UIElementType.TEXTFIELD,
                    inputLabel: t("createBranchCostConfig.visitCostHoliday"),
                    isRequired: true,
                })
            ),
        VistCostHolidayNight: z.union([z.literal(0), z.number().positive()])
            .nullable()
            .refine(value => value === null || value >= 0, {
                message: "If entered, the number must be positive or zero",
            }).describe(
                JSON.stringify({
                    inputType: "number",
                    elementType: UIElementType.TEXTFIELD,
                    inputLabel: t("createBranchCostConfig.visitCostHolidayNight"),
                    isRequired: true,
                })
            ),
    });
};

const getBranchCostConfigDefaultValue = (value = null) => {
    return {
        Branch: value && value.Branch ? value.Branch : null,
        MinuteCostDay: value && value.MinuteCostDay ? value.MinuteCostDay : 0,
        MinuteCostNight: value && value.MinuteCostNight ? value.MinuteCostNight : 0,
        MinuteCostHoliday: value && value.MinuteCostHoliday ? value.MinuteCostHoliday : 0,
        MinuteCostHolidayNight: value && value.MinuteCostHolidayNight ? value.MinuteCostHolidayNight : 0,
        VisitCost: value && value.VisitCost ? value.VisitCost : 0,
        VisitCostHoliday: value && value.VisitCostHoliday ? value.VisitCostHoliday : 0,
        VistCostHolidayNight: value && value.VistCostHolidayNight ? value.VistCostHolidayNight : 0,
        VistCostNight: value && value.VistCostNight ? value.VistCostNight : 0
    };
};

const getBranchCostConfigAPIPostSchema = (countryCode, businessCode) => {
    return {
        countryCode: countryCode,
        businessCode: businessCode,
        branchcostConfig: [
            {
                BranchId: "",
                MinuteCostDay: 0,
                MinuteCostNight: 0,
                MinuteCostHoliday: 0,
                MinuteCostHolidayNight: 0,
                VisitCost: 0,
                VisitCostHoliday: 0,
                VistCostHolidayNight: 0, /* Adjusting spelling mistake coming from backend API*/
                VistCostNight: 0, /* Adjusting spelling mistake coming from backend API*/
                BranchNumber: null,
                BranchName: "",
                Deleted: false,
                CountryCode: countryCode,
                BusinessCode: businessCode,
            },
        ],
    };
};

const getBranchCostConfigAPIDeleteSchema = (countryCode, businessCode) => {
    return {
        countryCode: countryCode,
        businessCode: businessCode,
        branchcostConfig: [
            {
                BranchId: "",
                Deleted: false,
                CountryCode: countryCode,
                BusinessCode: businessCode
            },
        ],
    };
};

const getBranchCostConfigAuditInfoSchema = () => {
    return {
        auditInfo: [
            {
                Location: "Branch Cost Config",
                LocationKey: "",
                Action: null,
                shortDesc: null,
                currentState: null,
                previousState: null,
            },
        ],
    };
};

export {
    getBranchCostConfigSchema,
    getBranchCostConfigDefaultValue,
    getBranchCostConfigAPIPostSchema,
    getBranchCostConfigAPIDeleteSchema,
    getBranchCostConfigAuditInfoSchema,
};
